import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Compass, Wind, Users, LayoutGrid } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getTimeTheme } from '../utils/timeTheme';
import { useContent } from '../context/ContentContext';

const navItems = [
  { path: '/', icon: Home, label: 'Today' },
  { path: '/explore', icon: Compass, label: 'Meditate' },
  { path: '/breathing', icon: Wind, label: 'Breathe' },
  { path: '/community', icon: Users, label: 'Community' },
  { path: '/more', icon: LayoutGrid, label: 'More', authRequired: true },
];

const BottomNav = () => {
  const location = useLocation();
  const { user, streak } = useAuth();
  const { getBreathingExerciseById } = useContent();
  const [theme, setTheme] = useState(() => getTimeTheme());
  useEffect(() => {
    const interval = setInterval(() => setTheme(getTimeTheme()), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Detect an active breathing exercise (`/breathing?exercise=<id>`) and
  // pulse the active nav pill at the exercise's cadence.
  const breathCycle = useMemo(() => {
    if (!location.pathname.startsWith('/breathing')) return null;
    const params = new URLSearchParams(location.search);
    const id = params.get('exercise');
    if (!id) return null;
    const ex = getBreathingExerciseById(id);
    if (!ex) return null;
    return (ex.inhale || 0) + (ex.hold || 0) + (ex.exhale || 0);
  }, [location.pathname, location.search]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    if (path === '/more') return location.pathname === '/more' || location.pathname === '/profile' || location.pathname === '/auth';
    if (path === '/manifest') return location.pathname.startsWith('/manifest');
    return location.pathname.startsWith(path);
  };

  const getProfilePath = () => (user ? '/more' : '/auth');

  return (
    <nav className="fixed bottom-0 z-50 glass border-t border-[#D6AD60]/20 w-full max-w-[640px] left-1/2 -translate-x-1/2">
      <div className="flex items-center justify-around px-2 h-[72px] max-w-lg mx-auto">
        {navItems.map((item) => {
          const active = isActive(item.path);
          const IconComponent = item.icon;
          const path = item.authRequired ? getProfilePath() : item.path;
          const showStreak = item.path === '/more' && user && streak.current > 0;

          return (
            <Link
              key={item.path}
              to={path}
              onClick={(e) => {
                // Any tap on the ALREADY-ACTIVE tab short-circuits — no
                // navigation, no re-mount, no page fade-in replay. This is
                // the fix that makes iOS/Android feel native: tapping the
                // current tab does nothing (matches iOS bottom-nav
                // behavior). Covers the previously-glitching case where a
                // user on `/manifest/<theme>` tapped Manifest and the whole
                // page cross-faded.
                if (active) {
                  e.preventDefault();
                  e.stopPropagation();
                  // Subtle haptic tick still fires so the tap feels alive.
                  if (typeof navigator !== 'undefined' && navigator.vibrate) {
                    try { navigator.vibrate(6); } catch { /* no-op */ }
                  }
                  return;
                }
                // Subtle haptic tick on cross-tab taps too.
                if (typeof navigator !== 'undefined' && navigator.vibrate) {
                  try { navigator.vibrate(6); } catch { /* no-op */ }
                }
              }}
              data-testid={`bottom-nav-${item.label.toLowerCase()}`}
              style={{
                // iOS/Android touch polish: kill the blue tap flash, block
                // double-tap-zoom-induced 300ms delay, and prevent text
                // selection ghosts on long-press.
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation',
                WebkitUserSelect: 'none',
                userSelect: 'none',
              }}
              className={`flex flex-col items-center gap-1 px-3 py-2 rounded-2xl transition-all duration-300 relative ${
                active
                  ? 'text-stone-800'
                  : 'text-stone-500 hover:text-[#D6AD60]'
              }`}
            >
              <div
                className={`w-10 h-10 rounded-2xl flex items-center justify-center transition-all duration-700 relative ${
                  active
                    ? 'bg-gradient-to-br from-[#1a1814] to-[#3a322a] shadow-md'
                    : 'text-inherit'
                }`}
                style={
                  active
                    ? {
                        color: theme.iconText,
                        boxShadow: `0 6px 18px ${theme.accentSoft}, inset 0 0 0 1px ${theme.accentSoft}`,
                        ...(breathCycle
                          ? {
                              animation: `nav-breath-glow ${breathCycle}s ease-in-out infinite`,
                            }
                          : null),
                      }
                    : undefined
                }
                data-testid={active ? `bottom-nav-active-${item.label.toLowerCase()}` : undefined}
                data-time-of-day={active ? theme.tod : undefined}
              >
                {item.path === '/more' && user ? (
                  <img
                    src={user.avatar || 'https://api.dicebear.com/7.x/avataaars/svg?seed=Default'}
                    alt=""
                    className={`w-6 h-6 rounded-full ${active ? 'ring-1 ring-white' : ''}`}
                  />
                ) : (
                  <IconComponent className="w-5 h-5" />
                )}
                {showStreak && (
                  <div className="absolute -top-1 -right-1 w-4.5 h-4.5 bg-orange-400 rounded-full flex items-center justify-center shadow-sm" style={{ width: '18px', height: '18px' }}>
                    <span className="text-[8px] font-bold text-white">{streak.current}</span>
                  </div>
                )}
              </div>
              <span
                className={`text-[10px] font-medium transition-all duration-300 ${
                  active ? 'text-stone-800' : 'text-stone-500'
                }`}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
      <style>{`
        @keyframes nav-breath-glow {
          0% {
            box-shadow: 0 4px 12px rgba(201, 168, 102, 0.15), inset 0 0 0 1px rgba(201, 168, 102, 0.25);
          }
          50% {
            box-shadow: 0 8px 30px rgba(218, 165, 32, 0.7), 0 0 22px rgba(218, 165, 32, 0.5), inset 0 0 0 1px rgba(218, 165, 32, 0.65);
          }
          100% {
            box-shadow: 0 4px 12px rgba(201, 168, 102, 0.15), inset 0 0 0 1px rgba(201, 168, 102, 0.25);
          }
        }
      `}</style>
    </nav>
  );
};

export default BottomNav;
